'use client';
import { useYearLowestPriceBadgeEnabled } from '@/domain/activity/black-friday-2026/YearLowestPriceBadgeContext';
import { IProductListItem } from '@/api/types/product';
import { IVipPrice, ListProduct, WishProduct } from '@/api/types/public';
import type { ArTryOnModelFields } from '@/api/types/tryon';
import { getArTryOnModelUrl, getVariantArTryOnModelFields } from '@/domain/tryon/model';
import { useStore } from '@/providers/app-store-provider';
import { colors, font } from '@/theme/zeelool.theme';
import { EProductImageType } from '@/utils/enums/goods';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Button } from '@mui/material';
import { ARTryOnOutlined, StarFilled } from '@zeelool/icon';
// import Link from 'next/link';
import AccessiblePrice from '@/components/AccessiblePrice';
import { useJump } from '@/components/MagicJump';
import {
  type HoverProductImagePriority,
  type ProductImagePriority,
  useProductImages,
} from '@/hooks/useProductImages';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { setSession } from '@/utils/tools';
import { useTranslations } from '@/i18n/use-translations';
import dynamic from 'next/dynamic';
import { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ResponsiveContext } from '../Responsive/ResponsiveProvider';
import WapOnly from '../Responsive/WapOnly';
import WebOnly from '../Responsive/WebOnly';
import Member from './Member';
import ProductCardMedia from './ProductCardMedia';
import Relate, { type CorrelationDisplayType, type CorrelationIndicatorMotion } from './Relate';
import SoldOutBlock from './SoldOutBlock';
import { formatNumber, getIntlLocale } from '@/utils/tools';
import { env } from '@/utils/env';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useSpmDomAttr, useSpmPosition, useSpmScope } from '@/reporting/spm';
import YearLowestPriceLabel from '@/domain/activity/black-friday-2026/components/YearLowestPriceLabel';
import type { ProductCardInput } from './types';
import { getYearLowestPriceText } from '@/domain/activity/black-friday-2026/utils/year-lowest-price';

const ClientOnlyPrice = dynamic(() => import('./Price'), { ssr: false });

export type Product = (ListProduct | WishProduct | IProductListItem) &
  ArTryOnModelFields & {
    is_year_lowest_price?: boolean;
    year_lowest_price_text?: string;
    stock?: number;
    tryon_image_url?: string;
    model_url?: string;
    image?: {
      product_gray?: string[];
    };
    correlation?: Array<
      ListProduct & {
        product_frame_color_main_url: string;
        is_year_lowest_price?: boolean;
        year_lowest_price_text?: string;
      }
    >;
    fine_ranking_type?: string;
    recall_group_type?: string;
    recommend_result_type?: string;
    spu_review_stars?: string;
    spu_review_count?: string;
    display_color_num?: number;
  };

export type CorrelationHoverBehavior = 'preview' | 'select';

const ProductCard = <
  T extends ProductCardInput & {
    correlation?: T[];
    is_year_lowest_price?: boolean;
    year_lowest_price_text?: string;
  },
>({
  product,
  className = '',
  isAcc = false,
  loginSituation = '游客收藏商品',
  removed,
  wished,
  showWishNum = false,
  showWish = true,
  showCorrelation = false,
  correlationDisplayType = 'color-swatch',
  correlationIndicatorMotion = 'static',
  correlationHoverBehavior = 'preview',
  showReviewStar = true,
  imageRule = [EProductImageType.PRODUCT, EProductImageType.MATERIAL, EProductImageType.CELEBRITY],
  imageHoverRule = [
    EProductImageType.MATERIAL,
    EProductImageType.CELEBRITY,
    EProductImageType.PRODUCT,
  ],
  productImagePriority,
  hoverProductImagePriority,
  showTryOn = true,
  mode = 'NORMAL',
  showMember = true,
  collectScene = '其它',
  index = 0,
  spmModule,
  productSpmCnt,
  handelClick = () => {},
  onInView = () => {},
  onValidExposure = () => {},
  imageClassName = '',
  productInfoClassName = '',
  isSquare = false,
  showCorrelationCount = 3,
  showTag = true,
  showOriginalPrice = true,
  nameSuffix = '',
  showName = true,
  namePriceInline = true,
  SDTryonClick,
  frameWidth = '80%',
  productImageWhiteBg = false,
  yearLowestPriceVariant = 'inline',
}: {
  product: T;
  className?: string;
  imageClassName?: string;
  loginSituation?: string;
  removed?: (product: T, index: number) => void;
  wished?: (product: T, index: number) => void;
  showWishNum?: boolean;
  showWish?: boolean;
  showCorrelation?: boolean;
  correlationDisplayType?: CorrelationDisplayType;
  /** 关联品选中标识的切换动效；默认静态，显式场景可启用滑动。 */
  correlationIndicatorMotion?: CorrelationIndicatorMotion;
  /** PC 关联品 hover 策略：临时预览或直接保留当前选择 */
  correlationHoverBehavior?: CorrelationHoverBehavior;
  imageRule?: EProductImageType[];
  imageHoverRule?: EProductImageType[];
  productImagePriority?: ProductImagePriority;
  hoverProductImagePriority?: HoverProductImagePriority;
  showTryOn?: boolean;
  isAcc?: boolean;
  showReviewStar?: boolean;
  showMember?: boolean;
  mode?: 'NORMAL' | 'MANAGE'; // 在最近浏览里用到，判断当前是否是manage编辑状态,编辑状态收藏不展示
  collectScene?: string;
  index?: number;
  /** 商品卡所在楼层的完整动态 SPM 模块编码（C 位，如 `${component_id}_${id}`）；收藏坑位码直接拼接，缺省不附加。 */
  spmModule?: string;
  /** 卡片主体坑位完整编码（A.B.C.D，如 `${spmModule}.product_001`）：与楼层点击/有效曝光
   * 事件的 spm_cnt 同源同值，传入即直接用于 DOM 打标（首页动态楼层码走此路径）；
   * 缺省回退按 spmModule 查作用域编码表（列表页/商详口径），都不命中不打标。 */
  productSpmCnt?: string;
  handelClick?: (index: number, product: T) => void;
  onInView?: (index: number, product: T) => void;
  onValidExposure?: (index: number, product: T) => void;
  isSquare?: boolean;
  productInfoClassName?: string;
  showCorrelationCount?: number; // 展示关联品数量
  showTag?: boolean;
  showOriginalPrice?: boolean; // 是否展示原价（划线价）
  nameSuffix?: string; // 商品名称后缀
  showName?: boolean; // 是否展示商品长名称
  namePriceInline?: boolean; // 是否将商品名称和价格放在同一行
  SDTryonClick?: (product: any, index: number) => void;
  frameWidth?: string;
  /** 商品图片容器是否使用白色背景，默认沿用列表灰底 */
  productImageWhiteBg?: boolean;
  /** badge 为活动样式，PC 放在价格旁，WAP 使用图片角标。 */
  yearLowestPriceVariant?: 'inline' | 'badge';
}) => {
  const tPriceA11y = useTranslations('goodsDetail.clientPrice');
  const locale = getIntlLocale(env.NEXT_PUBLIC_SITE_NAME);
  const token = useStore((state) => state.token);
  const userInfo = useStore((state) => state.userInfo);
  const handleTryonAction = useStore((state) => state.handleTryon);

  const currency = useStore((state) => state.currency);
  const [correlationIndex, setCorrelationIndex] = useState(0);
  const [currentProduct, setCurrentProduct] = useState<T>(product);
  const [originalCurrentProduct, setOriginalCurrentProduct] = useState<T>(product);
  const [myCorrelation, setMyCorrelation] = useState<T[]>(product.correlation || []);
  const [previewColorSku, setPreviewColorSku] = useState<string | null>(null);
  const labelProduct =
    yearLowestPriceVariant === 'badge' && previewColorSku !== null
      ? product.correlation?.find((item) => item.shop_sku === previewColorSku)
      : correlationIndex === 0
        ? product
        : product.correlation?.[correlationIndex];
  const badgeEnabled = useYearLowestPriceBadgeEnabled();
  const labelEnabled = yearLowestPriceVariant === 'inline' || badgeEnabled;
  const yearLowestPriceText = labelEnabled ? getYearLowestPriceText(labelProduct) : '';
  // 关联色缓存可能保留旧标签，回调必须使用当前实际展示的标签数据。
  const trackingProduct = useMemo(
    () => ({
      ...currentProduct,
      is_year_lowest_price: labelEnabled ? labelProduct?.is_year_lowest_price : false,
      year_lowest_price_text: labelEnabled ? labelProduct?.year_lowest_price_text : '',
    }),
    [
      currentProduct,
      labelEnabled,
      labelProduct?.is_year_lowest_price,
      labelProduct?.year_lowest_price_text,
    ],
  );

  const { deviceType } = useContext(ResponsiveContext);
  const waiMode = useStore((state) => state.waiMode);
  const isWeb = deviceType === EPlatform.WEB;
  if (isWeb) {
    showCorrelationCount = 6;
  }

  // 判断是否在服务端
  // const isServer = typeof window === 'undefined';

  // 根据环境选择Price组件
  // const PriceComponent = isServer ? Price : ClientOnlyPrice;
  const PriceComponent = ClientOnlyPrice;

  const toJumpTarget = useJump();
  const t = useTranslations('product');

  // 有效曝光去重（使用 ref 记录已上报的商品 SKU）
  const validExposedProductArray = useRef<string[]>([]);

  // 处理有效曝光回调（包含去重逻辑）
  // 使用 useCallback 确保 callback 能访问到最新的 currentProduct
  const handleValidExposure = useCallback(() => {
    // 去重：检查当前商品是否已上报过有效曝光
    // 注意：当商品切换时（如切换颜色），shop_sku 会变化，可以重新触发曝光
    if (!validExposedProductArray.current.includes(currentProduct.shop_sku)) {
      validExposedProductArray.current.push(currentProduct.shop_sku);
      // 双跑场景只保留 Valid 事件；未接入 Valid 事件的旧调用方仍通过有效曝光触发原事件。
      (onValidExposure || onInView)?.(index, trackingProduct);
    }
  }, [currentProduct, index, onInView, onValidExposure, trackingProduct]);

  const { ref: validExposureRef } = useValidExposure({
    callback: handleValidExposure,
    ts: currentProduct.shop_sku, // 当商品变化时，重置计时
  });

  // 收藏坑位（favorite_NNN）编码：spmModule 为编码表 C 位键且有作用域（列表/商详推荐/
  // 猜你喜欢等）时查表取完整 A.B.C.D，查表不命中不打标（表外静默，不发明编码）；无
  // 作用域（首页动态楼层码）时按原口径直拼完整模块码，收藏上报保持原 properties。
  const getSpmPosition = useSpmPosition();
  const spmScope = useSpmScope();
  const favoriteIndexKey = `favorite_${String(index + 1).padStart(3, '0')}`;
  const favoriteSpmCnt = spmScope
    ? getSpmPosition(spmModule, favoriteIndexKey)
    : spmModule
      ? `${spmModule}.${favoriteIndexKey}`
      : undefined;

  // 卡片主体坑位（product_NNN）编码：优先用楼层传入的完整编码（与楼层点击/有效曝光
  // 事件的 spm_cnt 同源同值）；未传时回退作用域查表（模块键 + 3 位序号，列表页/商详
  // 口径），序号 padStart 沿用上方既有写法。两者都不命中（首页未传、无作用域复用）时
  // 为 undefined，不打标（表外静默，严禁硬造编码）。
  const resolvedProductSpmCnt =
    productSpmCnt ??
    (spmModule
      ? getSpmPosition(spmModule, `product_${String(index + 1).padStart(3, '0')}`)
      : undefined);
  // Data SPM DOM 打标（请求头 data-spm=1 开关）：开关关闭或编码为空时 undefined，零 DOM 差异。
  const productDomSpm = useSpmDomAttr(resolvedProductSpmCnt);
  const tryOnDomSpm = useSpmDomAttr(
    SDTryonClick && spmModule
      ? getSpmPosition(spmModule, `try_on_${String(index + 1).padStart(3, '0')}`)
      : undefined,
  );

  // 色块点击仅切换卡片预览（本地状态），无既有埋点事件；spm 编码待数据团队确认事件名后接入。
  const handleCorrelationSelect = useCallback((swatchIndex: number) => {
    setCorrelationIndex(swatchIndex);
  }, []);

  const TryOnButton = () => {
    const modelUrl = getArTryOnModelUrl(currentProduct);
    if (
      showTryOn &&
      (currentProduct.tryon_image_url || modelUrl) &&
      currentProduct.is_glass === 1 &&
      mode === 'NORMAL'
    ) {
      let buttonText: string | ReactNode = '';
      if (modelUrl) {
        buttonText = (
          <>
            <ARTryOnOutlined className="mr-4 text-[14px]" />
            {t('tryOn.arTryOn')}
          </>
        );
      } else if (currentProduct.tryon_image_url) {
        buttonText = t('tryOn.tryOn');
      }
      return (
        <Button
          {...tryOnDomSpm}
          variant="contained"
          sx={{
            position: 'absolute',
            bottom: '10px',
            boxShadow: 'none',
            borderRadius: '50px',
            background: 'rgba(255,255,255,0.6)',
            color: colors.primaryBlack,
            fontSize: deviceType === EPlatform.WEB ? '12px' : '11px',
            fontFamily: font.br,
            left: '50%',
            lineHeight: '17px',
            padding: '0',
            height: deviceType === EPlatform.WEB ? '28px' : '24px',
            minWidth: deviceType === EPlatform.WEB ? '100px' : '87px',
            whiteSpace: 'nowrap',
            transform: 'translate(-50%, 0)',
            ':hover': {
              background:
                deviceType === EPlatform.WEB ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.6)',
            },
          }}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            handleTryon(currentProduct);
          }}
        >
          {buttonText}
        </Button>
      );
    }
    return <></>;
  };
  useEffect(() => {
    let newProduct: T;
    if (correlationIndex === 0) {
      newProduct = {
        ...product,
        ...myCorrelation[0],
        ...getVariantArTryOnModelFields(product, myCorrelation[0]),
        is_wish: product.is_wish,
      };
    } else {
      newProduct = myCorrelation[correlationIndex];
    }
    const handledProduct = {
      ...newProduct,
      fine_ranking_type: product.fine_ranking_type,
      recall_group_type: product.recall_group_type,
      recommend_result_type: product.recommend_result_type,
      spu_review_stars: product.spu_review_stars,
      spu_review_count: product.spu_review_count,
      correlation: product.correlation || [],
    };
    setCurrentProduct(handledProduct);
    setOriginalCurrentProduct(handledProduct);
    setPreviewColorSku(null);
  }, [correlationIndex, product.is_wish]);

  // 处理鼠标悬浮事件
  const handleMouseEnter = (hoverItem: T) => {
    if (correlationHoverBehavior === 'select') {
      const hoverIndex = myCorrelation.findIndex(
        (item) => item === hoverItem || item.shop_sku === hoverItem.shop_sku,
      );
      if (hoverIndex >= 0) setCorrelationIndex(hoverIndex);
      return;
    }

    if (yearLowestPriceVariant === 'badge') setPreviewColorSku(hoverItem.shop_sku ?? null);

    // 替换指定字段，使用安全的方式处理类型转换
    const updatedProduct = {
      ...currentProduct,
      image: hoverItem.image || currentProduct.image,
      products_promotions_type:
        hoverItem.products_promotions_type || currentProduct.products_promotions_type,
      is_new_in: hoverItem.is_new_in !== undefined ? hoverItem.is_new_in : currentProduct.is_new_in,
      is_wish: hoverItem.is_wish !== undefined ? hoverItem.is_wish : currentProduct.is_wish,
      is_hot: hoverItem.is_hot !== undefined ? hoverItem.is_hot : currentProduct.is_hot,
      status: hoverItem.status !== undefined ? hoverItem.status : currentProduct.status,
      stock: hoverItem.stock !== undefined ? hoverItem.stock : currentProduct.stock,
    };
    setCurrentProduct(updatedProduct);
  };

  // 处理鼠标移出事件
  const handleMouseLeave = () => {
    setPreviewColorSku(null);
    // 恢复原始的 currentProduct
    setCurrentProduct(originalCurrentProduct);
  };

  const showCorrelationModule = useMemo(() => {
    return myCorrelation?.length && showCorrelation && currentProduct.is_glass === 1;
  }, [myCorrelation, showCorrelation, currentProduct.is_glass]);

  // 试戴逻辑 - 直接调用 slice 中的业务方法
  const inAppH5 = [EPlatform.IOS, EPlatform.ANDROID].includes(deviceType);
  const handleTryon = async (_product: T) => {
    SDTryonClick?.(_product, index);
    await handleTryonAction(_product, inAppH5);
  };

  const onWished = useCallback(() => {
    // 当没有展示关联品时，直接修改currentProduct的is_wish
    if (!showCorrelation || myCorrelation.length === 0) {
      currentProduct.is_wish = 1;
    } else {
      // 当有关联品时，更新myCorrelation数组
      setMyCorrelation((prev) => {
        const newCorrelation = [...prev];
        const currentProductIndex = prev.findIndex(
          (item) => item.shop_sku === currentProduct.shop_sku,
        );

        // 确保找到了对应的产品索引
        if (currentProductIndex >= 0 && newCorrelation[currentProductIndex]) {
          newCorrelation[currentProductIndex].is_wish = 1;
        }

        return newCorrelation;
      });
    }
    wished?.(currentProduct, index);
  }, [currentProduct, index, showCorrelation, myCorrelation.length, wished]);

  const onRemoved = useCallback(() => {
    // 当没有展示关联品时，直接修改currentProduct的is_wish
    if (!showCorrelation || myCorrelation.length === 0) {
      currentProduct.is_wish = 2;
    } else {
      // 当有关联品时，更新myCorrelation数组
      setMyCorrelation((prev) => {
        const newCorrelation = [...prev];
        const currentProductIndex = prev.findIndex(
          (item) => item.shop_sku === currentProduct.shop_sku,
        );

        // 确保找到了对应的产品索引
        if (currentProductIndex >= 0 && newCorrelation[currentProductIndex]) {
          newCorrelation[currentProductIndex].is_wish = 2;
        }

        return newCorrelation;
      });
    }
    removed?.(currentProduct, index);
  }, [currentProduct, index, showCorrelation, myCorrelation.length, removed]);

  const { productImageSrc, hoverImageSrc } = useProductImages({
    productData: currentProduct,
    imageRule,
    imageHoverRule,
    productImagePriority,
    hoverProductImagePriority,
  });

  // 跳转到商品详情页
  const jumpToGoodsDetail = useCallback(() => {
    // event.stopPropagation();
    // event.preventDefault();
    // logger.warn('jumpToGoodsDetail', event);
    // logger.warn('jumpToGoodsDetail', currentProduct);

    let imgSrc = productImageSrc;
    // 如果是web端，并且有悬浮图片，则使用悬浮图片 并且是眼镜产品
    // 现在不用是眼镜产品也要跳转到 hover图片
    if (isWeb && hoverImageSrc) {
      imgSrc = hoverImageSrc;
    }
    setSession('zl-goods-detail-img-src', imgSrc);
    handelClick(index, trackingProduct);
    /// router.push(`/goods-detail/${currentProduct.shop_sku}`);

    setTimeout(() => {
      toJumpTarget({
        jump_type: 4,
        Jump_target: `/goods-detail/${currentProduct.shop_sku}`,
      });
    });
  }, [
    currentProduct,
    handelClick,
    hoverImageSrc,
    index,
    isWeb,
    productImageSrc,
    toJumpTarget,
    trackingProduct,
  ]);

  return (
    <div className={`${className} relative flex min-w-0 flex-col font-br`} ref={validExposureRef}>
      <div
        className="flex flex-1 cursor-pointer flex-col"
        onClick={jumpToGoodsDetail}
        {...productDomSpm}
      >
        <ProductCardMedia
          product={currentProduct}
          deviceType={deviceType}
          isAcc={isAcc}
          mode={mode}
          showWishNum={showWishNum}
          showWish={showWish}
          loginSituation={loginSituation}
          wished={onWished}
          removed={onRemoved}
          collectScene={collectScene}
          favoriteSpmCnt={favoriteSpmCnt}
          imageRule={imageRule}
          imageHoverRule={imageHoverRule}
          productImagePriority={productImagePriority}
          hoverProductImagePriority={hoverProductImagePriority}
          SoldOutBlock={<SoldOutBlock product={currentProduct} />}
          TryOnButton={<TryOnButton />}
          className={`shrink-0 ${imageClassName}`}
          isSquare={isSquare}
          showTag={showTag}
          frameWidth={frameWidth}
          productImageWhiteBg={productImageWhiteBg}
          topLeftLabel={
            !isWeb && yearLowestPriceVariant === 'badge' && yearLowestPriceText ? (
              <YearLowestPriceLabel text={yearLowestPriceText} variant="badge" size="card-mobile" />
            ) : undefined
          }
        />
        <WebOnly>
          <div
            className={`ProductInfo flex flex-1 flex-col pt-8 ${showCorrelationModule ? 'pb-10' : 'pb-0'} pl-16 pr-16 relative ${currentProduct.is_glass === 1 ? 'min-h-[84]' : 'min-h-[34]'} ${productInfoClassName}`}
          >
            <div
              className={
                namePriceInline
                  ? 'flex justify-between items-start'
                  : 'grid grid-cols-[minmax(0,1fr)_auto] items-start'
              }
            >
              <div
                data-product-summary
                className={`text-primary-black pt-6 min-h-[27px] ${
                  namePriceInline
                    ? 'flex min-w-0 flex-1 flex-row items-center flex-wrap gap-y-4'
                    : 'contents'
                }`}
              >
                <span
                  className={`text-16 mr-8 ${
                    namePriceInline ? '' : 'col-start-1 row-start-1 min-w-0 font-bb mb-[10px]'
                  }`}
                >
                  {currentProduct.short_name} {nameSuffix}
                </span>
                <div
                  className={
                    namePriceInline
                      ? 'contents'
                      : 'col-span-2 col-start-1 row-start-2 min-w-0 w-full'
                  }
                >
                  {token && userInfo.id && showMember ? (
                    <Member level={userInfo.member_level} productData={currentProduct} />
                  ) : null}
                  <PriceComponent
                    priceObject={currentProduct as IVipPrice}
                    renderPrice={(price, sellPrice) => (
                      <div
                        className={
                          namePriceInline
                            ? 'contents'
                            : 'flex max-w-full min-w-0 flex-wrap items-center gap-x-8 gap-y-4'
                        }
                      >
                        <span
                          className={`inline-flex max-w-full shrink-0 flex-wrap items-center gap-x-8 ${namePriceInline ? 'mr-8' : ''}`}
                        >
                          <AccessiblePrice
                            value={sellPrice}
                            label={tPriceA11y('srCurrent')}
                            className={`whitespace-nowrap text-16 ${namePriceInline ? 'font-bb' : ''} ${waiMode ? 'text-[#cc1f1f]' : ''}`}
                          />
                          {showOriginalPrice && price && (
                            <AccessiblePrice
                              value={price}
                              label={tPriceA11y('srOriginal')}
                              className={`whitespace-nowrap text-14 line-through ${waiMode ? 'text-primary-black' : 'text-secondary-text'}`}
                            />
                          )}
                        </span>
                        {yearLowestPriceVariant === 'inline' ? (
                          <YearLowestPriceLabel text={yearLowestPriceText} size="desktop" />
                        ) : (
                          <YearLowestPriceLabel
                            text={yearLowestPriceText}
                            variant="campaign-price"
                          />
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
              <div
                data-product-review
                className={`pt-4 flex shrink-0 ml-16 ${namePriceInline ? '' : 'col-start-2 row-start-1'}`}
              >
                {currentProduct.spu_review_stars && showReviewStar ? (
                  <>
                    <StarFilled className="text-[16px] text-primary-black mr-[8px]" />
                    <span className="text-primary-black text-14">
                      {formatNumber(currentProduct.spu_review_stars || 5, 1, locale)}
                    </span>
                  </>
                ) : null}
              </div>
            </div>
            {currentProduct.is_glass === 1 && showName ? (
              <div
                data-product-description
                className={`mt-3 text-12 w-full  overflow-ellipsis overflow-hidden whitespace-nowrap text-left ${waiMode ? 'text-primary-black' : 'text-auxiliary-dark-gray'}`}
              >
                {currentProduct.name}
              </div>
            ) : null}
            {showCorrelationModule ? (
              <div data-product-correlations className="mt-auto">
                <Relate
                  productData={product}
                  correlation={myCorrelation}
                  correlationIndex={correlationIndex}
                  onClick={handleCorrelationSelect}
                  showCorrelationCount={showCorrelationCount}
                  displayType={correlationDisplayType}
                  indicatorMotion={correlationIndicatorMotion}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={
                    correlationHoverBehavior === 'preview' ? handleMouseLeave : undefined
                  }
                />
              </div>
            ) : null}
          </div>
        </WebOnly>
        <WapOnly>
          <div
            className={`ProductInfo flex flex-1 flex-col pt-10 pb-0 pl-8 pr-8 relative ${productInfoClassName}`}
          >
            <div className="flex justify-between items-center">
              <div className="flex items-center text-primary-black leading-[14px] text-left">
                <span
                  className={`text-12 ${waiMode ? 'text-primary-black' : 'text-secondary-text'} leading-[14px]`}
                >
                  {currentProduct.short_name} {nameSuffix}
                </span>
              </div>
              <div className="flex items-center height-[14px]">
                {currentProduct.spu_review_stars && showReviewStar ? (
                  <>
                    <StarFilled className="text-[11px] text-primary-black mr-[4px]" />
                    <span className="text-primary-black text-12 leading-[14px] pt-2">
                      {formatPriceNumberWithSymbol(
                        currentProduct.spu_review_stars,
                        currency,
                        true,
                        1,
                        true,
                      )}
                    </span>
                  </>
                ) : null}
              </div>
            </div>
            <div className="mt-6 flex min-w-0 flex-wrap items-center gap-x-8 gap-y-6">
              {token && userInfo.id && showMember ? (
                <Member
                  className="!mr-0 shrink-0"
                  level={userInfo.member_level}
                  productData={currentProduct}
                />
              ) : null}
              <PriceComponent
                priceObject={currentProduct as IVipPrice}
                renderPrice={(price, sellPrice) => (
                  <>
                    <span className="inline-flex max-w-full shrink-0 flex-wrap items-center gap-x-[6px]">
                      <AccessiblePrice
                        value={sellPrice}
                        label={tPriceA11y('srCurrent')}
                        className="shrink-0 whitespace-nowrap text-14 font-bb leading-[17px] notranslate"
                      />
                      {showOriginalPrice && price && (
                        <AccessiblePrice
                          value={price}
                          label={tPriceA11y('srOriginal')}
                          className={`shrink-0 whitespace-nowrap text-12 leading-[17px] line-through ${waiMode ? 'text-primary-black' : 'text-secondary-text'}`}
                        />
                      )}
                    </span>
                    {yearLowestPriceVariant === 'inline' ? (
                      <YearLowestPriceLabel text={yearLowestPriceText} />
                    ) : null}
                  </>
                )}
              />
            </div>
            {product.correlation?.length && showCorrelation && currentProduct.is_glass === 1 ? (
              <div data-product-correlations className="mt-auto">
                <Relate
                  productData={product}
                  correlation={myCorrelation}
                  correlationIndex={correlationIndex}
                  onClick={handleCorrelationSelect}
                  showCorrelationCount={showCorrelationCount}
                  displayType={correlationDisplayType}
                  indicatorMotion={correlationIndicatorMotion}
                />
              </div>
            ) : null}
          </div>
        </WapOnly>
      </div>
    </div>
  );
};

export default ProductCard;
